import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TablePagination,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Flag as FlagIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { currencyAPI, flagsAPI } from '../services/api';

const CurrencyManagement = () => {
  const [currencies, setCurrencies] = useState([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [flags, setFlags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to get decimal places based on currency name
  const getDecimalPlaces = (currencyName) => {
    if (!currencyName) return 4;
    const upperName = currencyName.toUpperCase();
    return (upperName.includes('IDR') || upperName.includes('VND')) ? 5 : 4;
  };
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    order: '',
    buying: '',
    selling: '',
    flag: ''
  });
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [historyDialog, setHistoryDialog] = useState({ open: false, currency: null });
  const [currencyHistory, setCurrencyHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkEditData, setBulkEditData] = useState([]);
  const [bulkEditOriginalData, setBulkEditOriginalData] = useState([]);

  // Fetch currencies from backend
  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const data = await currencyAPI.getAll();
      
      // Transform backend data to frontend format
      const transformedData = data.map(item => ({
        id: item.id,
        name: item.name,
        order: item.display_order || 0,
        buying: parseFloat(item.buying_rate || 0),
        selling: parseFloat(item.selling_rate || 0),
        status: item.currency_status || 'active',
        flag: item.flag || ''
      }));
      
      // Sort currencies: active first, then by display order
      const sortedData = transformedData.sort((a, b) => {
        // First, sort by status (active first)
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        
        // Then sort by display order within the same status
        return a.order - b.order;
      });
      
      setCurrencies(sortedData);
      setFilteredCurrencies(sortedData);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch currencies: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch available flags from backend
  const fetchFlags = async () => {
    try {
      const data = await flagsAPI.getAll();
      setFlags(data);
    } catch (error) {
      console.error('Error fetching flags:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch flags: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Fetch currency history
  const fetchCurrencyHistory = async (currencyId) => {
    setHistoryLoading(true);
    try {
      const data = await currencyAPI.getHistory(currencyId);
      setCurrencyHistory(data);
    } catch (error) {
      console.error('Error fetching currency history:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch currency history: ' + error.message,
        severity: 'error'
      });
    } finally {
      setHistoryLoading(false);
    }
  };
  
  useEffect(() => {
    // check the credential
    const token = localStorage.getItem('adminToken');
    console.log(token)
    
    const checkCredentials = async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/token/${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok){
          // redirect to login
          
         
        }
        } 
        
      if (token) {
        console.log("Token found, checking credentials...");
        checkCredentials();
      } 
    
  }, []);

  // Load currencies on component mount
  useEffect(() => {
    fetchCurrencies();
    fetchFlags();
  }, []);

  // Filter currencies based on search term
  useEffect(() => {
    const filtered = currencies.filter(currency =>
      currency.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCurrencies(filtered);
    setPage(0);
  }, [searchTerm, currencies]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (currency = null) => {
    if (currency) {
      setEditingCurrency(currency);
      setFormData({
        name: currency.name,
        order: currency.order.toString(),
        buying: currency.buying.toString(),
        selling: currency.selling.toString(),
        flag: currency.flag || ''
      });
    } else {
      setEditingCurrency(null);
      setFormData({
        name: '',
        order: '',
        buying: '',
        selling: '',
        flag: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCurrency(null);
    setFormData({
      name: '',
      order: '',
      buying: '',
      selling: '',
      flag: ''
    });
  };

  const handleFormChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value
    });
  };

  const handleSubmit = async () => {
    // Validate form data
    if (!formData.name || !formData.order || !formData.buying || !formData.selling) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error'
      });
      return;
    }


    // Validate numeric fields
    if (isNaN(formData.order) || isNaN(formData.buying) || isNaN(formData.selling)) {
      setSnackbar({
        open: true,
        message: 'Order, buying rate, and selling rate must be valid numbers',
        severity: 'error'
      });
      return;
    }

    setSubmitLoading(true);
    
    const currencyData = {
      name: formData.name.trim(),
      order: parseInt(formData.order),
      buying: parseFloat(formData.buying),
      selling: parseFloat(formData.selling),
      flag: formData.flag,
      status: editingCurrency ? editingCurrency.status : 'active'
    };

    try {
      if (editingCurrency) {
        // Update existing currency
        await currencyAPI.update(editingCurrency.id, currencyData);
        setSnackbar({
          open: true,
          message: 'Currency updated successfully',
          severity: 'success'
        });
      } else {
        // Add new currency
        await currencyAPI.create(currencyData);
        setSnackbar({
          open: true,
          message: 'Currency added successfully',
          severity: 'success'
        });
      }
      
      // Refresh the currencies list
      await fetchCurrencies();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving currency:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save currency: ' + error.message,
        severity: 'error'
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (currencyId) => {
    if (!window.confirm('Are you sure you want to delete this currency?')) {
      return;
    }

    try {
      setLoading(true);
      await currencyAPI.delete(currencyId);
      
      // Refresh the currencies list
      await fetchCurrencies();
      
      setSnackbar({
        open: true,
        message: 'Currency deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting currency:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete currency: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (currency) => {
    const newStatus = currency.status === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} ${currency.name}?`)) {
      return;
    }

    try {
      setLoading(true);
      
      // Create updated currency data with new status
      const currencyData = {
        name: currency.name,
        order: currency.order,
        buying: currency.buying,
        selling: currency.selling,
        flag: currency.flag,
        status: newStatus
      };
      
      await currencyAPI.update(currency.id, currencyData);
      
      // Refresh the currencies list
      await fetchCurrencies();
      
      setSnackbar({
        open: true,
        message: `Currency ${action}d successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling currency status:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${action} currency: ` + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };


  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenHistoryDialog = (currency) => {
    setHistoryDialog({ open: true, currency });
    fetchCurrencyHistory(currency.id);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialog({ open: false, currency: null });
    setCurrencyHistory([]);
  };

  const handleOpenBulkEdit = () => {
    // Initialize bulk edit data with current active currencies
    const activeCurrencies = currencies
      .filter(c => c.status === 'active')
      .map(currency => ({
        id: currency.id,
        name: currency.name,
        buying: currency.buying.toString(),
        selling: currency.selling.toString(),
        flag: currency.flag
      }));
    setBulkEditData(activeCurrencies);
    // Store original values for comparison
    setBulkEditOriginalData(JSON.parse(JSON.stringify(activeCurrencies)));
    setBulkEditDialog(true);
  };

  const handleCloseBulkEdit = () => {
    setBulkEditDialog(false);
    setBulkEditData([]);
    setBulkEditOriginalData([]);
  };

  const handleBulkEditChange = (index, field, value) => {
    const updatedData = [...bulkEditData];
    updatedData[index][field] = value;
    setBulkEditData(updatedData);
  };

  const handleBulkEditSubmit = async () => {
    // Validate all fields
    for (const currency of bulkEditData) {
      if (isNaN(currency.buying) || isNaN(currency.selling)) {
        setSnackbar({
          open: true,
          message: `Invalid rates for ${currency.name}. Please enter valid numbers.`,
          severity: 'error'
        });
        return;
      }
    }

    // Filter only currencies that have changed
    const changedCurrencies = bulkEditData.filter((currency, index) => {
      const original = bulkEditOriginalData[index];
      return currency.buying !== original.buying || currency.selling !== original.selling;
    });

    if (changedCurrencies.length === 0) {
      setSnackbar({
        open: true,
        message: 'No changes detected',
        severity: 'info'
      });
      handleCloseBulkEdit();
      return;
    }

    setSubmitLoading(true);
    
    try {
      // Update only changed currencies
      const updatePromises = changedCurrencies.map(async (currency) => {
        const currencyData = {
          name: currency.name,
          order: currencies.find(c => c.id === currency.id).order,
          buying: parseFloat(currency.buying),
          selling: parseFloat(currency.selling),
          flag: currency.flag,
          status: 'active'
        };
        return currencyAPI.update(currency.id, currencyData);
      });

      await Promise.all(updatePromises);
      
      setSnackbar({
        open: true,
        message: `Successfully updated ${changedCurrencies.length} ${changedCurrencies.length === 1 ? 'currency' : 'currencies'}`,
        severity: 'success'
      });
      
      // Refresh the currencies list
      await fetchCurrencies();
      handleCloseBulkEdit();
    } catch (error) {
      console.error('Error bulk updating currencies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update currencies: ' + error.message,
        severity: 'error'
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const paginatedCurrencies = filteredCurrencies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  console.log(paginatedCurrencies)

  return (
    <Box sx={{ p: 3 }}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" component="h1" gutterBottom>
            Currency Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredCurrencies.length} entries found • Click any row to view update history
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenBulkEdit}
            sx={{ textTransform: 'none' }}
          >
            Bulk Edit Prices
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ textTransform: 'none' }}
          >
            Add New Currency
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search currencies..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
              <Chip 
                label={`Total: ${currencies.length}`} 
                color="primary" 
                variant="outlined" 
              />
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                sx={{ textTransform: 'none' }}
                onClick={fetchCurrencies}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Currency Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Currency Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Flag</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Order</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Buying Rate</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Selling Rate</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCurrencies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {loading ? 'Loading currencies...' : 'No currencies found. Add a new currency to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCurrencies.map((currency) => (
                  <TableRow 
                    key={currency.id} 
                    hover
                    onClick={() => handleOpenHistoryDialog(currency)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                    title="Click to view currency update history"
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {currency.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {currency.flag ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img 
                            src={`${process.env.REACT_APP_API_URL}/picture/flags/${currency.flag}`}
                            alt={`${currency.name} flag`}
                            style={{ 
                              width: '24px', 
                              height: '16px', 
                              objectFit: 'cover',
                              borderRadius: '2px',
                              border: '1px solid #e0e0e0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {currency.flag}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FlagIcon sx={{ color: '#ccc', fontSize: '20px' }} />
                          <Typography variant="caption" color="text.secondary">
                            No flag
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={currency.order} 
                        size="small" 
                        color="default" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="success.main">
                        {currency.buying.toFixed(getDecimalPlaces(currency.name))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="error.main">
                        {currency.selling.toFixed(getDecimalPlaces(currency.name))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleStatus(currency);
                        }}
                        sx={{ mr: 1 }}
                        color={currency.status === 'active' ? 'success' : 'default'}
                        title={currency.status === 'active' ? 'Deactivate currency' : 'Activate currency'}
                      >
                        {currency.status === 'active' ? (
                          <ToggleOnIcon fontSize="small" />
                        ) : (
                          <ToggleOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(currency)}
                        sx={{ mr: 1 }}
                        color="primary"
                        title="Edit currency"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(currency.id)}
                        color="error"
                        title="Delete currency"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCurrencies.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Currency Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCurrency ? 'Edit Currency' : 'Add New Currency'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Currency Name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder="e.g., USD 100, EUR 50, Bitcoin, Ethereum"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="flag-select-label">Flag</InputLabel>
                <Select
                  labelId="flag-select-label"
                  label="Flag"
                  name="flag"
                  value={formData.flag}
                  onChange={handleFormChange}
                  renderValue={(selected) => {
                    if (!selected) return 'Select a flag';
                    
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img 
                          src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/picture/flags/${selected}`}
                          alt="Selected flag"
                          style={{ 
                            width: '24px', 
                            height: '16px', 
                            objectFit: 'cover',
                            borderRadius: '2px',
                            border: '1px solid #e0e0e0'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Typography>{selected}</Typography>
                      </Box>
                    );
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FlagIcon sx={{ color: '#ccc', fontSize: '20px' }} />
                      <Typography>No flag</Typography>
                    </Box>
                  </MenuItem>
                  {flags.map((flag) => (
                    <MenuItem key={flag.filename} value={flag.filename}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img 
                          src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/picture/flags/${flag.filename}`}
                          alt={flag.name || flag.filename}
                          style={{ 
                            width: '24px', 
                            height: '16px', 
                            objectFit: 'cover',
                            borderRadius: '2px',
                            border: '1px solid #e0e0e0'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Typography>{flag.name || flag.filename}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Order"
                name="order"
                type="number"
                value={formData.order}
                onChange={handleFormChange}
                placeholder="1"
                required
                helperText="Lower numbers appear first"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* Empty space for layout */}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Buying Rate"
                name="buying"
                type="number"
                step="0.00001"
                value={formData.buying}
                onChange={handleFormChange}
                placeholder="0.00000"
                required
                helperText="Rate for buying this currency"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Selling Rate"
                name="selling"
                type="number"
                step="0.00001"
                value={formData.selling}
                onChange={handleFormChange}
                placeholder="0.00000"
                required
                helperText="Rate for selling this currency"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={submitLoading}
            startIcon={submitLoading ? <CircularProgress size={20} /> : null}
          >
            {submitLoading ? 'Saving...' : (editingCurrency ? 'Update' : 'Add')} Currency
          </Button>
        </DialogActions>
      </Dialog>

      {/* Currency History Dialog */}
      <Dialog 
        open={historyDialog.open} 
        onClose={handleCloseHistoryDialog} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          Update History - {historyDialog.currency?.name}
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : currencyHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No update history found for this currency.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Display Order</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Buying Rate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Selling Rate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Flag</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currencyHistory.map((history, index) => (
                    <TableRow key={history.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(history.changed_at).toLocaleString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {history.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={history.display_order} 
                          size="small" 
                          color="default" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="success.main">
                          {parseFloat(history.buying_rate).toFixed(getDecimalPlaces(history.name))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error.main">
                          {parseFloat(history.selling_rate).toFixed(getDecimalPlaces(history.name))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {history.flag ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <img 
                              src={`${process.env.REACT_APP_API_URL}/picture/flags/${history.flag}`}
                              alt={`${history.name} flag`}
                              style={{ 
                                width: '24px', 
                                height: '16px', 
                                objectFit: 'cover',
                                borderRadius: '2px',
                                border: '1px solid #e0e0e0'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {history.flag}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FlagIcon sx={{ color: '#ccc', fontSize: '20px' }} />
                            <Typography variant="caption" color="text.secondary">
                              No flag
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog 
        open={bulkEditDialog} 
        onClose={handleCloseBulkEdit} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Bulk Edit Currency Prices
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit buying and selling rates for multiple currencies at once. Only active currencies are shown.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Flag</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Buying Rate</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Selling Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkEditData.map((currency, index) => (
                  <TableRow key={currency.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {currency.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {currency.flag ? (
                        <img 
                          src={`${process.env.REACT_APP_API_URL}/picture/flags/${currency.flag}`}
                          alt={`${currency.name} flag`}
                          style={{ 
                            width: '24px', 
                            height: '16px', 
                            objectFit: 'cover',
                            borderRadius: '2px',
                            border: '1px solid #e0e0e0'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <FlagIcon sx={{ color: '#ccc', fontSize: '20px' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        step="0.00001"
                        value={currency.buying}
                        onChange={(e) => handleBulkEditChange(index, 'buying', e.target.value)}
                        sx={{ width: '140px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        step="0.00001"
                        value={currency.selling}
                        onChange={(e) => handleBulkEditChange(index, 'selling', e.target.value)}
                        sx={{ width: '140px' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {bulkEditData.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No active currencies to edit.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkEdit}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkEditSubmit} 
            variant="contained"
            disabled={submitLoading || bulkEditData.length === 0}
            startIcon={submitLoading ? <CircularProgress size={20} /> : null}
          >
            {submitLoading ? 'Updating...' : `Update ${bulkEditData.length} Currencies`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CurrencyManagement;
